"use client";

import { useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { Disclaimer } from "@/components/disclaimer";
import { computeDripProjection } from "@/lib/calculators/dividend-reinvestment";
import { formatCompactCurrency, formatCurrency } from "@/lib/format";

function parseAmount(draft: string): number {
  const n = Number(draft.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-tabular text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

const LEGEND_LABELS: Record<string, string> = {
  valueWithReinvest: "Mit Dividenden-Reinvestition",
  valueWithoutReinvest: "Ohne Reinvestition (nur Kurswert)",
};

/**
 * Dividenden-Reinvestitions-Rechner (backlog.md -> Weiterer Nutzwert-Katalog) - für
 * Akademie/Dividenden-Tracker-Kontext, aktien-unabhängig, ohne Login. Rein hypothetische
 * Modellrechnung, siehe lib/calculators/dividend-reinvestment.ts. Zeigt bewusst zwei
 * Linien (mit/ohne Reinvestition) nebeneinander, um den Zinseszinseffekt reinvestierter
 * Dividenden sichtbar zu machen - das ist der eigentliche Lerninhalt dieses Rechners.
 */
export function DividendReinvestmentCalculator() {
  const [initialInvestment, setInitialInvestment] = useState("10000");
  const [monthlyContribution, setMonthlyContribution] = useState("100");
  const [initialPricePerShare, setInitialPricePerShare] = useState("50");
  const [annualPriceGrowthPercent, setAnnualPriceGrowthPercent] = useState("5");
  const [initialDividendYieldPercent, setInitialDividendYieldPercent] = useState("3");
  const [annualDividendGrowthPercent, setAnnualDividendGrowthPercent] = useState("5");
  const [years, setYears] = useState(20);

  const result = useMemo(
    () =>
      computeDripProjection({
        initialInvestment: parseAmount(initialInvestment),
        monthlyContribution: parseAmount(monthlyContribution),
        initialPricePerShare: parseAmount(initialPricePerShare),
        annualPriceGrowthRate: parseAmount(annualPriceGrowthPercent) / 100,
        initialDividendYield: parseAmount(initialDividendYieldPercent) / 100,
        annualDividendGrowthRate: parseAmount(annualDividendGrowthPercent) / 100,
        years,
      }),
    [
      initialInvestment,
      monthlyContribution,
      initialPricePerShare,
      annualPriceGrowthPercent,
      initialDividendYieldPercent,
      annualDividendGrowthPercent,
      years,
    ],
  );

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline p-4">
      <div>
        <span className="font-serif text-lg font-semibold text-ink">Dividenden-Reinvestitions-Rechner</span>
        <p className="mt-1 text-xs text-ink-secondary">
          Rein hypothetische Modellrechnung mit angenommenen, konstanten Raten - keine
          Prognose für eine konkrete Aktie oder einen konkreten ETF.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Startinvestition (€)
          <Input type="number" min={0} value={initialInvestment} onChange={(e) => setInitialInvestment(e.target.value)} className="bg-card" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Sparrate/Monat (€)
          <Input type="number" min={0} value={monthlyContribution} onChange={(e) => setMonthlyContribution(e.target.value)} className="bg-card" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Startkurs je Anteil (€)
          <Input type="number" min={0.01} step="0.01" value={initialPricePerShare} onChange={(e) => setInitialPricePerShare(e.target.value)} className="bg-card" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Angenommenes Kurswachstum (%/Jahr)
          <Input type="number" value={annualPriceGrowthPercent} onChange={(e) => setAnnualPriceGrowthPercent(e.target.value)} className="bg-card" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Dividendenrendite (%)
          <Input type="number" min={0.1} step="0.1" value={initialDividendYieldPercent} onChange={(e) => setInitialDividendYieldPercent(e.target.value)} className="bg-card" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Dividendenwachstum (%/Jahr)
          <Input type="number" value={annualDividendGrowthPercent} onChange={(e) => setAnnualDividendGrowthPercent(e.target.value)} className="bg-card" />
        </label>
        <label className="col-span-2 flex flex-col gap-1 text-xs text-muted-foreground sm:col-span-3">
          Anlagedauer (Jahre)
          <select
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            className="rounded-md border border-hairline bg-card px-2 py-1.5 text-sm text-ink"
          >
            {[5, 10, 15, 20, 25, 30, 35, 40].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatTile label="Endwert mit Reinvestition" value={formatCurrency(result.finalValueWithReinvest, "EUR")} />
            <StatTile label="Endwert ohne Reinvestition" value={formatCurrency(result.finalValueWithoutReinvest, "EUR")} />
            <StatTile label="Reinvestierte Dividenden gesamt" value={formatCurrency(result.totalDividendsReinvested, "EUR")} />
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={result.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="dripWithReinvest" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C6A15B" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C6A15B" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="dripWithoutReinvest" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16265C" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#16265C" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#16265C" strokeOpacity={0.14} strokeDasharray="3 5" vertical={false} />
                <XAxis dataKey="year" tickFormatter={(y) => `${y}J`} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <YAxis
                  domain={[0, "auto"]}
                  tickFormatter={(v) => formatCompactCurrency(v, "EUR")}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <Tooltip
                  formatter={(value, name) => [formatCurrency(Number(value), "EUR"), LEGEND_LABELS[name as string] ?? name]}
                  labelFormatter={(y) => `Jahr ${y}`}
                />
                <Legend formatter={(name) => LEGEND_LABELS[name as string] ?? name} wrapperStyle={{ fontSize: 11 }} />
                <Area type="linear" dataKey="valueWithReinvest" stroke="#C6A15B" strokeWidth={2} fill="url(#dripWithReinvest)" />
                <Area type="linear" dataKey="valueWithoutReinvest" stroke="#16265C" strokeWidth={1.5} fill="url(#dripWithoutReinvest)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[0.65rem] text-muted-foreground">
            Gold = Depotwert bei Reinvestition der Dividenden in weitere Anteile, Navy =
            derselbe Depotwert, wenn Dividenden stattdessen ausgezahlt (nicht angelegt)
            würden. Dividenden werden vereinfachend einmal jährlich angenommen.
          </p>
        </>
      )}

      <Disclaimer />
    </div>
  );
}
